import type { MyReviewState } from "../../lib/marketplace-connection-api";
import { reviewAskView, type ReviewAnswer } from "../../lib/review-ask";
import type { ProRole } from "../marketplace/ReviewedProAction.helpers";
import { ReviewedProAction } from "../marketplace/ReviewedProAction";
import { SectionLabel } from "../marketplace/OffersInbox";

/**
 * ReviewPrompt — the connected stack's right-column review ask (plan 435
 * M4, mfh-review-prompt; roadmap M4 bullet 4; design doc §5.3).
 *
 * Mounted by `ConnectedStack` only while the server offers an ask
 * (`next_ask`, see `lib/review-ask.ts`), and kept mounted after a submit so
 * the confirmation stays on screen. It renders the SAME `ReviewedProAction`
 * every other surface uses, so Home can never offer an ask the Team card or
 * the check-in modal already answered.
 *
 * Two copy variants, chosen by the ask — the closed ask gets the
 * congratulatory frame (the deal is done, ask while it's fresh), the
 * worked_together ask gets the plain "how's it going" frame. Decision 527
 * (2026-10-07): the top-level `HomeHero` headline stays the standard
 * connected copy in EVERY case — only this card's own headline changes, and
 * the closed variant carries no reset / "new search" affordance.
 */
export interface ReviewPromptProps {
  proName: string;
  proUserId: string;
  role?: ProRole;
  state: MyReviewState | undefined;
  incentiveLabel?: string;
  onSubmit: (answer: ReviewAnswer) => Promise<void>;
}

const CLOSED_HEADLINE = "🎉 Congratulations!";
const CLOSED_SUBTEXT = (proName: string) =>
  `While it's fresh — how was working with ${proName}?`;
const WORKED_TOGETHER_HEADLINE = (proName: string) =>
  `How's it going with ${proName}?`;
const WORKED_TOGETHER_SUBTEXT =
  "A quick review helps other buyers choose with confidence.";
const POSTED_HEADLINE = "Thanks for your review";

export function ReviewPrompt({
  proName,
  proUserId,
  role,
  state,
  incentiveLabel,
  onSubmit,
}: ReviewPromptProps) {
  const view = reviewAskView(state);
  const isClosed = view.kind === "close";
  const answered = view.kind === "readonly";

  return (
    <div
      className="marketplace-surface space-y-3 p-4 sm:p-5"
      data-testid="home-review-prompt"
      data-variant={answered ? "answered" : isClosed ? "closed" : "worked-together"}
    >
      <SectionLabel label="Leave a review" />
      <p
        className="text-base font-semibold text-foreground"
        data-testid="review-prompt-headline"
      >
        {answered ? POSTED_HEADLINE : isClosed ? CLOSED_HEADLINE : WORKED_TOGETHER_HEADLINE(proName)}
      </p>
      {!answered && (
        <p
          className="text-sm text-muted-foreground"
          data-testid="review-prompt-subtext"
        >
          {isClosed ? CLOSED_SUBTEXT(proName) : WORKED_TOGETHER_SUBTEXT}
        </p>
      )}
      <ReviewedProAction
        incentiveLabel={incentiveLabel}
        onSubmit={onSubmit}
        proName={proName}
        proUserId={proUserId}
        role={role}
        state={state}
      />
    </div>
  );
}
